import React, { useState, useEffect } from 'react';
import './App.css';
import './tailwind.css';
import LandingPage from './Views/LandingPage';
import CopyForm from './components/form/copy-form';
import CopyResult from './components/result/copy-result';
import ResultButtonGroup from './components/result/result-button-group';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import SignupPage from './Views/SignupPage';
import LoginPage from './Views/LoginPage';
import { auth } from './firebaseConfig';
import { useAuth } from './contexts/AuthContext';
import SidebarNav from './components/nav/SidebarNav';
import { getUserDetails } from './clients/userClient';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import PersonaModal from './components/questionaire/persona-modal';
import Notification from './components/tailwind/notification';
import Generations from './components/history/generations';
import ToolSelector from './components/form/tool-selector';
import LinkedInCopyForm from './components/form/linkedin-copy-form';
import VoiceCopyForm from './components/form/voice-copy-form';

const stripePromise = loadStripe('pk_live_51PBKZbIY1U5P4c7Van963AOsCf7mTiSLRE2w3UtuNXkz5rbhqiJJnDXQMISb7XxxIAERgv2KbhjOFhgAfevCDD86009YrKoaJv')
// const stripePromise = loadStripe('pk_test_51PBKZbIY1U5P4c7V880tp2wewj7Iyw3Og80DhtjvysgFvcReHxcBB32nriEmdpf7XRzKJxGM6iK6JrS9ARREIy9Z00i7LjxS7H')

function App() {
  const [copy, setCopy] = useState();
  const [user, setUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [userEmailAddress, setUserEmailAddress] = useState('');
  const [openSubscriptionModal, setOpenSubscriptionModal] = useState(false);
  const [openPersonaModal, setOpenPersonaModal] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [isLoadingUserDetails, setIsLoadingUserDetails] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedTool, setSelectedTool] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { authToken } = useAuth();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      setIsRefreshing(true);
      if (currentUser) {
        try {
          const idTokenResult = await currentUser.getIdTokenResult();
          if (idTokenResult.claims.exp * 1000 < Date.now()) {
            await auth.signOut();
            resetAllStates();
            if (location.pathname !== '/login') {
              navigate('/login');
            }
          } else {
            setUser(currentUser);
            setUserEmailAddress(currentUser.email);
            if (location.pathname !== '/' && location.pathname !== '/generations') {
              navigate('/');
            }
          }
        } catch (error) {
          console.error('Error fetching ID token', error);
        }
      } else {
        resetAllStates();
        if (isLoggingOut) {
          if (location.pathname !== '/login') {
            navigate('/login');
          }
          setIsLoggingOut(false);
        } else if (location.pathname !== '/signup' && location.pathname !== '/login' && location.pathname !== '/') {
          navigate('/signup');
        }
      }
      setIsLoading(false);
      setIsRefreshing(false);
      setInitialLoading(false);
    });

    return () => unsubscribe();
  }, [navigate, location.pathname, isLoggingOut]);

  useEffect(() => {
    if (userEmailAddress && authToken) {
      setIsRefreshing(true);
      setIsLoadingUserDetails(true);
      refreshUserDetails();
    }
  }, [userEmailAddress, authToken]);

  const showCopyNotification = () => {
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  function updateCopy(newCopy) {
    setCopy(newCopy);
    refreshUserDetails();
  }

  function refreshUserDetails() {
    getUserDetails(userEmailAddress, authToken)
      .then(async response => {
        if (response.status === 401) {
          await auth.signOut();
          resetAllStates();
          if (location.pathname !== '/login') {
            navigate('/login');
          }
          throw new Error('Unauthorized');
        }
        return response.json();
      })
      .then((details) => {
        setUserDetails(details);
        setIsLoadingUserDetails(false);
        setIsRefreshing(false);
        if (details?.professionalRole === undefined && details?.referralSource === undefined) {
          setOpenPersonaModal(true);
        }
      })
  }

  function clearCopy() {
    setCopy(null);
  }

  const logout = async () => {
    await auth.signOut();
    resetAllStates();
    setIsLoggingOut(true); // Set the logout state
    navigate('/login');
  };

  function resetAllStates() {
    setCopy(null);
    setUser(null);
    setUserDetails(null);
    setIsLoadingUserDetails(false);
    setIsLoading(false);
    setIsLoggingOut(false);
    setUserEmailAddress('');
  }

  if (initialLoading) {
    return (
      <div className="App min-h-screen">
      <div className="flex justify-center items-center h-screen">
      <svg className="animate-spin h-14 w-14 text-custom-orange" viewBox="0 0 24 24">
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
          fill="none"
        ></circle>
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
      </svg>
      </div>
      </div>
    );
  }

  if (!user || !authToken || !userEmailAddress) {
    return (
      <div className="min-h-screen p-6">
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/" element={<LandingPage />} />
        </Routes>
      </div>
    );
  }

  return (
    <div className="App bg-custom-grey min-h-screen">
      <div>
        {openPersonaModal && <PersonaModal email={userEmailAddress} isOpen={openPersonaModal} onRequestClose={() => setOpenPersonaModal(false)} setUserDetails={setUserDetails} />}
        <SidebarNav logout={logout} stripePromise={stripePromise} user={userDetails} openSubscriptionModal={openSubscriptionModal} setOpenSubscriptionModal={setOpenSubscriptionModal} />
      </div>
      <Routes path="/">
      <Route index element={<>
  {!isLoadingUserDetails && userDetails?.credits !== undefined ? (
    selectedTool === 'generic-voice' ? (
      <div className="w-10/12 flex flex-col justify-between items-center mx-auto p-1">
        {showNotification && <Notification />}
        <div className="w-full mx-auto bg-white shadow-2xl max-w-4xl mt-8 p-5 sm:p-10">
          <div style={{ display: copy ? 'none' : 'block' }}>
            <VoiceCopyForm
              updateCopy={updateCopy}
              email={userEmailAddress}
              setOpenSubscriptionModal={setOpenSubscriptionModal}
              credits={userDetails?.credits}
              setSelectedTool={setSelectedTool}
            />
          </div>
          {copy &&
            <CopyResult
              copy={copy}
              updateCopy={updateCopy} email={userEmailAddress}
              setOpenSubscriptionModal={setOpenSubscriptionModal}
              credits={userDetails?.credits}
            />
          }
        </div>
        {copy && <ResultButtonGroup editPrompt={clearCopy} copy={copy} showCopyNotification={showCopyNotification} />}
      </div>
    ) : selectedTool === 'generic' ? (
      <div className="w-10/12 flex flex-col justify-between items-center mx-auto p-1">
        {showNotification && <Notification />}
        <div className="w-full mx-auto bg-white shadow-2xl max-w-4xl mt-8 p-5 sm:p-10">
          <div style={{ display: copy ? 'none' : 'block' }}>
            <CopyForm
              updateCopy={updateCopy}
              email={userEmailAddress}
              setOpenSubscriptionModal={setOpenSubscriptionModal}
              credits={userDetails?.credits}
              setSelectedTool={setSelectedTool}
            />
          </div>
          {copy &&
            <CopyResult
              copy={copy}
              updateCopy={updateCopy} email={userEmailAddress}
              setOpenSubscriptionModal={setOpenSubscriptionModal}
              credits={userDetails?.credits}
            />
          }
        </div>
        {copy && <ResultButtonGroup editPrompt={clearCopy} copy={copy} showCopyNotification={showCopyNotification} />}
      </div>
    ) : selectedTool === 'linkedin-voice' ? (
      <div className="w-10/12 flex flex-col justify-between items-center mx-auto p-1">
        {showNotification && <Notification />}
        <div className="w-full mx-auto bg-white shadow-2xl max-w-4xl mt-8 p-5 sm:p-10">
          <div style={{ display: copy ? 'none' : 'block' }}>
            <LinkedInCopyForm
              updateCopy={updateCopy}
              email={userEmailAddress}
              setOpenSubscriptionModal={setOpenSubscriptionModal}
              credits={userDetails?.credits}
              setSelectedTool={setSelectedTool}
            />
          </div>
          {copy &&
            <CopyResult
              copy={copy}
              updateCopy={updateCopy} email={userEmailAddress}
              setOpenSubscriptionModal={setOpenSubscriptionModal}
              credits={userDetails?.credits}
            />
          }
        </div>
        {copy && <ResultButtonGroup editPrompt={clearCopy} copy={copy} showCopyNotification={showCopyNotification} />}
      </div>
    ) : (
<div className="w-10/12 flex flex-col justify-center items-center h-screen mx-auto p-2 space-y-10">
  <div className="flex-grow" />
  <ToolSelector setSelectedTool={setSelectedTool} />
  <div className="flex-grow" />
  <div className="flex-grow" />
</div>
    )
  ) : (
    <div className="flex justify-center items-center h-screen">
      <svg className="animate-spin h-14 w-14 text-custom-orange" viewBox="0 0 24 24">
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
          fill="none"
        ></circle>
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
      </svg>
    </div>
  )}
</>}
/>
        <Route path="generations" element={<Generations userEmailAddress={userEmailAddress}/>} />
      </Routes>
    </div>
  );
}

export default App;